// Lightweight client-side error logger that fans out to the server function.
// Safe in components, hooks, and error boundaries. Always fire-and-forget.
// NOTE: import the server fn statically — the server-fn plugin replaces it with
// a client-safe RPC stub. A dynamic import of "@/server/*" is blocked by
// import-protection and crashes the dev server transform.
import { recordError } from "@/lib/errors.functions";



let lastFlushAt = 0;
const queue: Array<{
  level: "error" | "warn" | "info";
  source: string;
  message: string;
  stack?: string;
  route?: string;
  ctx?: Record<string, unknown>;
}> = [];

async function flush() {
  if (typeof window === "undefined") return;
  if (queue.length === 0) return;
  const now = Date.now();
  if (now - lastFlushAt < 500) return;
  lastFlushAt = now;
  const batch = queue.splice(0, queue.length).slice(0, 20);
  try {
    // Only flush when there's an authenticated session — recordError requires auth.
    const { supabase } = await import("@/integrations/supabase/client");
    const { data } = await supabase.auth.getSession();
    if (!data.session) return;
    await Promise.all(
      batch.map((e) =>
        recordError({
          data: {
            level: e.level,
            source: e.source.slice(0, 100),
            message: (e.message ?? "").slice(0, 4000),
            stack: e.stack?.slice(0, 8000),
            route: e.route?.slice(0, 300),
            context: e.ctx ?? {},
          },
        }).catch(() => {}),
      ),
    );
  } catch {
    // network or auth failure — drop silently
  }
}

export function logClientError(input: {
  level?: "error" | "warn" | "info";
  source: string;
  message: string;
  stack?: string;
  route?: string;
  ctx?: Record<string, unknown>;
}) {
  try {
    queue.push({
      level: input.level ?? "error",
      source: input.source,
      message: input.message,
      stack: input.stack,
      route: input.route ?? (typeof window !== "undefined" ? window.location.pathname : undefined),
      ctx: input.ctx,
    });
    setTimeout(flush, 250);
  } catch {
    /* swallow */
  }
}

// Global handlers — installed once.
let installed = false;
export function installGlobalErrorHandlers() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("error", (e) => {
    logClientError({
      source: "window.error",
      message: String(e.message ?? "Unknown error"),
      stack: e.error?.stack,
    });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason: any = e.reason;
    const msg = String(reason?.message ?? reason ?? "Unhandled rejection");
    const isChunkErr = /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk \d+ failed/i.test(msg);
    // Don't spam the remote logger with dev-server chunk hiccups.
    if (!isChunkErr) {
      logClientError({
        source: "unhandledrejection",
        message: msg,
        stack: reason?.stack,
      });
    }
    if (isChunkErr) {
      try {
        const KEY = "__lex_chunk_reload";
        if (!sessionStorage.getItem(KEY)) {
          sessionStorage.setItem(KEY, "1");
          setTimeout(() => window.location.reload(), 250);
        }
      } catch { /* ignore */ }
    }
  });
}

